import React from 'react';
import { Link } from 'react-router-dom';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import { withStyles } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import AddIcon from '@material-ui/icons/Add';
import CompareIcon from '@material-ui/icons/Compare';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import Skeleton from '@material-ui/lab/Skeleton';

import Header from '../../components/utils/Header';
import Scenario from '../../api/Scenario';
import User from '../../api/User';
import ScenarioMoreMenu from './ScenarioMoreMenu';
import { cycle } from '../../store/Cycle';
import {
  chainAsyncCalls,
  formatDate,
  formatFloat,
  formatRatioPercent
} from '../../utils';
import ScenarioTypeIcon from './ScenarioTypeIcon';
import { statGreen, statRed, statOrange, statGrey } from '../../globals';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import currentUser from '../../store/currentUser';
import PermissionChecker from '../../components/permissions/PermissionChecker';
import ForbiddenView from '../../views/ForbiddenView';

const styles = theme => ({
  baseScenario: {
    backgroundColor: `${fade(theme.palette.primary.main, 0.1)} !important`,
  },
  validatedScenario: {
    backgroundColor: `${fade(theme.palette.secondary.main, 0.1)} !important`,
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
  select: {
    minWidth: theme.spacing(20),
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  paper: {
    borderRadius: 10,
    overflow: 'hidden',
  },
  tableTitle: {
    padding: theme.spacing(2),
  },
  scenarioName: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  grey: {
    color: '#727272',
    fontSize: 12,
  },
  tableKPIgrey: {
    color: '#7e7e7e',
    padding: '6px 0px',
    width: 90,
    borderRadius: 5,
    fontWeight: 900,
    backgroundColor: 'rgba(205, 205, 205, 0.5)',
  },
  tableKPIgreen: {
    color: statGreen,
    padding: '6px 0px',
    width: 90,
    borderRadius: 5,
    fontWeight: 900,
    backgroundColor: 'rgba(56, 173, 169, 0.2)',
  },
  tableKPIred: {
    color: statRed,
    padding: '6px 0px',
    width: 90,
    borderRadius: 5,
    fontWeight: 900,
    backgroundColor: 'rgba(252, 92, 101, 0.2)',
  },
  tableKPIorange: {
    color: statOrange,
    padding: '6px 0px',
    width: 90,
    borderRadius: 5,
    fontWeight: 900,
    backgroundColor: 'rgba(255, 193, 55, 0.2)',
  },
  nameWidth: {
    minWidth: 350,
  },
  clickCell: {
    cursor: 'pointer',
  },
  runStatus: {
    backgroundColor: fade(statGrey, 0.2),
    color: statGrey,
    borderRadius: 5,
    fontWeight: 900,
    padding: theme.spacing(0.5, 1),
    textAlign: 'center',
  },
  runStatusFailed: {
    backgroundColor: fade(statRed, 0.2),
    color: statRed,
  },
  expandIcon: {
    order: -1,
    '&.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  },
});


@withStyles(styles)
@observer
class ScenarioListView extends React.Component {
  @observable updating = true;
  @observable scenarios = [];
  @observable user = '';
  @observable users = [];
  @observable data = null;

  async componentDidMount() {
    await this.updateData();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.location !== prevProps.location) {
      this.updateData();
    }
  }

  @action updateData = chainAsyncCalls(async () => {
    this.updating = true;
    [this.scenarios, this.users] = await Promise.all([
      Scenario.getList({
        ...cycle.params,
        created_by: this.user,
      }),
      User.getList(),
    ]);
    this.updating = false;
  });

  @action onUserChange = event => {
    this.user = event.target.value;
    this.updateData();
  };

  permissionControl(list) {
    return !currentUser.is_superuser && currentUser.can('athena.scenarios_limited') ? list.filter(l => [0, '0', 1, '1', 2, '2'].includes(l.type)) : list;
  }


  getRunStatus(scenario) {
    const { classes } = this.props;
    if (scenario.isFail) {
      return <div className={`${classes.runStatus} ${classes.runStatusFailed}`}>Run failed</div>;
    }
    return <div className={classes.runStatus}>Run in progress</div>;
  }

  getDate = date => (
    <Grid container direction="column">
      <Grid item>
        <strong>{formatDate(date)}</strong>
      </Grid>
      <Grid item className={this.props.classes.grey}>
        <span>at <strong>{date.toLocaleTimeString('en-US')}</strong></span>
      </Grid>
    </Grid>
  );

  formatKPIExternal = (kpiData, conditionalFormatName) => {
    if (!kpiData) {
      return null;
    }
    return (
      <Grid container spacing={1} wrap="nowrap">
        <Grid item>
          <ConditionalFormat
            value={kpiData.value}
            name={conditionalFormatName}
            variant="rect"
          >
            {
              kpiData.unit === '%'
                ? formatRatioPercent(kpiData.value)
                : `${formatFloat(kpiData.value)} ${kpiData.unit}`
            }
          </ConditionalFormat>
        </Grid>
      </Grid>
    )
  };

  renderScenarioRow = (scenario) => {
    const { classes } = this.props;
    const clickCell = currentUser.can('athena.view_scenario')
      ? {
        onClick: () => document.location = scenario.detailUrl,
        className: classes.clickCell,
      }
      : {};
    const children = this.scenarios.filter(otherScenario => otherScenario.parent === scenario.id);
    return (
      <React.Fragment key={scenario.id}>
        <TableRow hover>
          <TableCell {...clickCell}>
            <ScenarioTypeIcon scenario={scenario} />
          </TableCell>
          <TableCell {...clickCell}>
            <Grid container direction="column">
              <Grid item className={classes.scenarioName}>
                <span>{scenario.toString()}</span>
              </Grid>
              <Grid item className={classes.grey}>
                <span>
                  By <strong>
                      {
                        scenario.createdByUser === null
                          ? 'Unknown'
                          : scenario.createdByUser.toString()
                      }
                    </strong>
                </span>
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right" {...clickCell}>
            {this.getDate(new Date(scenario.created_at))}
          </TableCell>
          {
            scenario.isSuccess
            ? (
              <>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.sales_kpi, 'sales-kpi')}
                </TableCell>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.costs_kpi, 'costs-kpi')}
                </TableCell>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.availability_fg_kpi, 'availability-fg-kpi')}
                </TableCell>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.capacity_sfg_kpi, 'capacity-sfg-kpi')}
                </TableCell>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.capacity_fg_kpi, 'capacity-fg-kpi')}
                </TableCell>
                <TableCell align="center" {...clickCell}>
                  {this.formatKPIExternal(scenario.stocks_kpi, 'stocks-kpi')}
                </TableCell>
              </>
            )
              : (
              <TableCell colSpan={6} {...clickCell}>
                {this.getRunStatus(scenario)}
              </TableCell>
            )
          }
          <TableCell size="small" align="right">
            <ScenarioMoreMenu scenario={scenario} onUpdate={this.updateData}/>
          </TableCell>
        </TableRow>
        {children.length >= 1 && (
          <>
            <TableRow hover>
              <TableCell colSpan={10}>
                <Accordion>
                  <AccordionSummary expandIcon={<KeyboardArrowRightIcon />} classes={{ expandIcon: classes.expandIcon }}>
                    {children.length} reduced scope scenarios
                  </AccordionSummary>
                  <AccordionDetails>
                    {this.renderTable(this.permissionControl(children), false)}
                  </AccordionDetails>
                </Accordion>
              </TableCell>
            </TableRow>
          </>
        )}
      </React.Fragment>
    );
  };

  renderTable(scenarios) {
    const { classes } = this.props;
    return (
      <Table className={classes.noWrap}>
        <TableHead>
          <TableRow>
            <TableCell>
              {Scenario.fieldsData.type.label}
            </TableCell>
            <TableCell className={classes.nameWidth}>
              {Scenario.fieldsData.name.label}
            </TableCell>
            <TableCell align="right">
              {Scenario.fieldsData.created_at.label}
            </TableCell>
            <TableCell>
              {Scenario.fieldsData.sales_kpi.label}
            </TableCell>
            <TableCell>
              {Scenario.fieldsData.costs_kpi.label}
            </TableCell>
            <TableCell>
              {Scenario.fieldsData.availability_fg_kpi.label}
            </TableCell>
            <TableCell>
              {Scenario.fieldsData.capacity_sfg_kpi.label}
            </TableCell>
            <TableCell>
              {Scenario.fieldsData.capacity_fg_kpi.label}
            </TableCell>
            <TableCell>
               Stocks
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {
            scenarios.length === 0
              ? (
                <TableRow>
                  <TableCell colSpan={10} className={classes.disabled}>
                    <Typography align="center">
                      No scenario for this cycle.
                    </Typography>
                  </TableCell>
                </TableRow>
              )
              : scenarios.map(scenario => this.renderScenarioRow(scenario))
          }
        </TableBody>
      </Table>
    );
  }

  get table() {
    if ((Scenario.fieldsData === null) || this.updating) {
      return <Skeleton variant="rect" height={550} />
    }
    const { classes } = this.props;
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.tableTitle}>Available Scenarios</Typography>
        {this.renderTable(this.permissionControl(this.scenarios.filter(scenario => scenario.parent === null)))}
      </Paper>
    );
  }

  render() {
    if (this.updating) {
      return <Skeleton variant="rect" height={550} />
    }
    if (Scenario.fieldsData === null) {
      return <ForbiddenView />
    }
    // const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={2}>
        <Grid item container spacing={2} justify="space-between" alignItems="center">
          <Grid item>
            <Header title="Scenarios" />
          </Grid>
          <Grid item>
            <Grid container spacing={2} alignItems="center" wrap="nowrap">
             {/* <Grid item>
                <TextField
                  select
                  label="Created By"
                  value={this.user}
                  variant="outlined"
                  className={classes.select}
                  onChange={this.onUserChange}
                >
                  {this.users.map(user =>
                    <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
                  )}
                </TextField>
              </Grid> */}
              <Grid item>
                <PermissionChecker permission="athena.view_scenario">
                  {
                    hasPermission =>
                      <Button
                        variant="contained"
                        startIcon={<CompareIcon viewBox="2 2 20 20"/>}
                        component={Link}
                        to="/scenarios/comparison"
                        disabled={!hasPermission}
                      >
                        Compare
                      </Button>
                  }
                </PermissionChecker>
              </Grid>
              <Grid item>
                <PermissionChecker permission="athena.add_scenario">
                  {
                    hasPermission =>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon  viewBox="2 2 20 20"/>}
                        component={Link}
                        to="/scenarios/create"
                        disabled={!hasPermission}
                      >
                        Create
                      </Button>
                  }
                </PermissionChecker>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {this.table}
        </Grid>
      </Grid>
    );
  }
}


export default ScenarioListView;
